import { useEffect, useState } from "react";
import { ArrowRight, CalendarDays, CheckCircle2, GraduationCap, Loader2, Mail, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { FadeIn } from "@/components/Reveal";

const TOPICS = [
  { title: "Business risk management", desc: "Spot the operational risks and gaps that quietly hurt small businesses." },
  { title: "Workforce documentation & I-9 processes", desc: "Understand employer responsibilities and keep workforce records in order." },
  { title: "Business insurance awareness", desc: "Learn what common policies do — and what they don't." },
  { title: "Licensing and permit tracking", desc: "Keep required licenses, permits, and renewals from lapsing." },
  { title: "OSHA & workplace safety awareness", desc: "Build safety awareness and documentation habits for your workplace." },
  { title: "HIPAA awareness", desc: "For applicable healthcare businesses — handling information with care." },
  { title: "Contract administration & document management", desc: "Organize agreements, obligations, and renewals (tracking — not drafting)." },
  { title: "SOP development", desc: "Turn tribal knowledge into repeatable, documented procedures." },
  { title: "Audit preparation", desc: "Get ready for client, lender, or regulator requests before they arrive." },
  { title: "Using technology to manage operations", desc: "Practical tools — including MITIVISE — for organizing your business." },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const WorkshopCard = ({ w }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setMsg("Please check the consent box so we can confirm your registration.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setMsg("");
    try {
      const res = await api.post(`/api/tsm4/workshops/${w.id}/register`, form);
      setStatus("success");
      setMsg(res.data?.message || "You're registered! A confirmation will come from our team.");
    } catch (err) {
      setStatus("error");
      setMsg(formatApiErrorDetail(err.response?.data) || "Registration failed — please try again.");
    }
  };

  return (
    <article className="flex h-full flex-col rounded-xl border border-navy/10 bg-white p-7" data-testid="workshop-card">
      <div className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-tealdeep">
        <CalendarDays className="h-4 w-4" />
        {fmtDate(w.starts_at)}
      </div>
      <h3 className="mt-4 text-xl font-extrabold tracking-tight text-navy" data-testid="workshop-card-title">{w.title}</h3>
      {w.location && (
        <p className="mt-2 flex items-center gap-2 text-sm text-ink/60">
          <MapPin className="h-4 w-4 text-tealdeep" /> {w.location}
        </p>
      )}
      {w.description && <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/70">{w.description}</p>}

      {status === "success" ? (
        <p className="mt-6 flex items-start gap-2 rounded-sm border border-teal/30 bg-teal/5 px-4 py-3 text-sm font-semibold text-tealdeep" data-testid="workshop-registration-success">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {msg}
        </p>
      ) : open ? (
        <form onSubmit={submit} className="mt-6 space-y-4 border-t border-navy/10 pt-5" data-testid="workshop-registration-form">
          <div>
            <Label htmlFor={`ws-name-${w.id}`} className="field-label">Full Name *</Label>
            <Input id={`ws-name-${w.id}`} required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="field-input" placeholder="Jane Doe" data-testid="workshop-input-name" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor={`ws-email-${w.id}`} className="field-label">Email *</Label>
              <Input id={`ws-email-${w.id}`} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" placeholder="you@yourbusiness.com" data-testid="workshop-input-email" />
            </div>
            <div>
              <Label htmlFor={`ws-phone-${w.id}`} className="field-label">Phone</Label>
              <Input id={`ws-phone-${w.id}`} type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field-input" placeholder="(216) 555-0100" data-testid="workshop-input-phone" />
            </div>
          </div>
          <label className="flex items-start gap-2.5 text-xs leading-relaxed text-ink/60">
            <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: v === true })} className="mt-0.5 border-navy/30 data-[state=checked]:border-teal data-[state=checked]:bg-teal" data-testid="workshop-input-consent" />
            <span>I agree to allow TSM Group CLE to contact me about this workshop. *</span>
          </label>
          {status === "error" && msg && <p className="text-sm text-red-700" role="alert" data-testid="workshop-registration-error">{msg}</p>}
          <button type="submit" disabled={status === "submitting"} className="btn-gold w-full justify-center" data-testid="workshop-register-button">
            {status === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Reserve My Spot
          </button>
        </form>
      ) : (
        <button type="button" onClick={() => setOpen(true)} disabled={!w.registration_open} className="btn-gold mt-6 w-full justify-center disabled:cursor-not-allowed disabled:opacity-50" data-testid="workshop-register-open">
          {w.registration_open ? (<>Register for This Workshop<ArrowRight className="h-4 w-4" /></>) : "Registration Closed"}
        </button>
      )}
    </article>
  );
};

const UpdatesList = () => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setStatus("submitting");
    try {
      await api.post("/api/tsm4/workshop-subscribe", { email, consent });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="rounded-xl border border-teal/25 bg-white p-7" data-testid="workshop-updates-card">
      <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-tealdeep">
        <Mail className="h-4 w-4" /> Workshop Updates
      </p>
      {status === "success" ? (
        <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-tealdeep" data-testid="workshop-updates-success">
          <CheckCircle2 className="h-4 w-4" /> You're subscribed — we'll let you know about new sessions.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-4 flex flex-col gap-4 sm:flex-row" data-testid="workshop-updates-form">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourbusiness.com" className="field-input flex-1" data-testid="workshop-updates-email" aria-label="Email for workshop updates" />
          <button type="submit" disabled={!consent || status === "submitting"} className="btn-gold !px-6 disabled:cursor-not-allowed disabled:opacity-50" data-testid="workshop-updates-submit">
            Get Updates
          </button>
        </form>
      )}
      {status !== "success" && (
        <label className="mt-4 flex items-start gap-2.5 text-xs leading-relaxed text-ink/60">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5 border-navy/30 data-[state=checked]:border-teal data-[state=checked]:bg-teal" data-testid="workshop-updates-consent" />
          <span>Email me workshop announcements and business education from TSM Group CLE. Unsubscribe anytime — we never sell your information. *</span>
        </label>
      )}
    </div>
  );
};

const CheckupForm = () => {
  const [form, setForm] = useState({ full_name: "", business_name: "", email: "", phone: "", focus: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setMsg("Please check the consent box so TSM Group CLE can contact you.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setMsg("");
    try {
      await api.post("/api/tsm4/checkup", form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setMsg(formatApiErrorDetail(err.response?.data) || "Submission failed — please try again.");
    }
  };

  return (
    <div className="rounded-xl border border-navy/10 bg-white p-7 lg:p-9" data-testid="checkup-card">
      <p className="eyebrow">Complimentary</p>
      <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-navy">Business Document Checkup</h3>
      <p className="mt-3 text-sm leading-relaxed text-ink/70">
        A complimentary review conversation about how your business documents — licenses, insurance records,
        contracts, workforce files, and procedures — are organized. We'll highlight gaps and quick wins. No legal
        advice, no obligation.
      </p>
      {status === "success" ? (
        <p className="mt-6 flex items-start gap-2 rounded-sm border border-teal/30 bg-teal/5 px-4 py-3 text-sm font-semibold text-tealdeep" data-testid="checkup-success">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> Request received — our team will reach out to schedule your checkup.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-6 grid gap-x-6 gap-y-5 sm:grid-cols-2" data-testid="checkup-form">
          <div>
            <Label htmlFor="ck-name" className="field-label">Contact Name *</Label>
            <Input id="ck-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="field-input" placeholder="Jane Doe" data-testid="checkup-input-name" />
          </div>
          <div>
            <Label htmlFor="ck-business" className="field-label">Business Name</Label>
            <Input id="ck-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="field-input" placeholder="Your Business LLC" data-testid="checkup-input-business" />
          </div>
          <div>
            <Label htmlFor="ck-email" className="field-label">Email *</Label>
            <Input id="ck-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" placeholder="name@company.com" data-testid="checkup-input-email" />
          </div>
          <div>
            <Label htmlFor="ck-phone" className="field-label">Phone *</Label>
            <Input id="ck-phone" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field-input" placeholder="(216) 555-0100" data-testid="checkup-input-phone" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ck-focus" className="field-label">What should we look at?</Label>
            <Textarea id="ck-focus" value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} className="field-input min-h-20" placeholder="e.g., licenses and renewals, workforce files, contracts, SOPs" data-testid="checkup-input-focus" />
          </div>
          <label className="flex items-start gap-2.5 text-xs leading-relaxed text-ink/60 sm:col-span-2">
            <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: v === true })} className="mt-0.5 border-navy/30 data-[state=checked]:border-teal data-[state=checked]:bg-teal" data-testid="checkup-input-consent" />
            <span>I agree to allow TSM Group CLE to contact me about my checkup request. *</span>
          </label>
          {status === "error" && msg && <p className="text-sm text-red-700 sm:col-span-2" role="alert" data-testid="checkup-error">{msg}</p>}
          <button type="submit" disabled={status === "submitting"} className="btn-gold w-full justify-center sm:col-span-2" data-testid="checkup-submit-button">
            {status === "submitting" ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Request My Complimentary Checkup<ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      )}
    </div>
  );
};

export default function WorkshopsPage() {
  const [workshops, setWorkshops] = useState(null);

  useEffect(() => {
    document.title = "Workshops & Resources | Small Business Education | TSM Group CLE";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Practical workshops and resources for small business owners: risk management, I-9 processes, insurance awareness, licensing, SOPs, audit preparation, and more. Request a complimentary Business Document Checkup.");
    api.get("/api/tsm4/workshops").then((r) => setWorkshops(r.data)).catch(() => setWorkshops([]));
  }, []);

  return (
    <div className="bg-paper" data-testid="workshops-page">
      <PageHero
        testid="workshops-hero"
        eyebrow="Education for Owners"
        title="Workshops & Resources"
        copy="Starting a business comes with responsibilities nobody teaches you. TSM Group CLE breaks down risk, documentation, and compliance topics in plain language — so you know what to manage and how."
      >
        <div className="mt-9 flex flex-col gap-4 sm:flex-row">
          <a href="#upcoming" data-testid="workshops-hero-events-cta" className="btn-gold">See Upcoming Workshops</a>
          <a href="#checkup" data-testid="workshops-hero-checkup-cta" className="btn-outline-light">Free Document Checkup</a>
        </div>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="container-tsm">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">What We Cover</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="workshops-topics-headline">
              The responsibilities behind running a business — explained.
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="workshops-topics-grid">
            {TOPICS.map((t, i) => (
              <FadeIn key={t.title} delay={Math.min(i * 0.05, 0.3)} className="h-full">
                <div className="h-full rounded-xl border border-navy/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-teal/40">
                  <GraduationCap className="h-5 w-5 text-teal" />
                  <h3 className="mt-4 text-base font-extrabold text-navy">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/65">{t.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section id="upcoming" className="border-y border-navy/5 bg-white py-24 lg:py-32">
        <div className="container-tsm">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">Upcoming Workshops</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="workshops-upcoming-headline">
              Reserve your seat.
            </h2>
          </FadeIn>

          {workshops === null ? (
            <p className="mt-10 text-ink/50" data-testid="workshops-loading">Loading upcoming workshops…</p>
          ) : workshops.length === 0 ? (
            <FadeIn delay={0.1}>
              <div className="mt-10 rounded-xl border border-dashed border-navy/25 bg-paper px-8 py-14 text-center" data-testid="workshops-empty">
                <CalendarDays className="mx-auto h-8 w-8 text-tealdeep" />
                <h3 className="mt-5 text-xl font-extrabold text-navy">No workshops are scheduled right now.</h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink/60">
                  New sessions are announced here first. Join the updates list below and we'll let you know as soon
                  as the next workshop is on the calendar.
                </p>
              </div>
            </FadeIn>
          ) : (
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="workshops-grid">
              {workshops.map((w) => (
                <FadeIn key={w.id} className="h-full">
                  <WorkshopCard w={w} />
                </FadeIn>
              ))}
            </div>
          )}

          <FadeIn delay={0.15} className="mt-12">
            <UpdatesList />
          </FadeIn>
        </div>
      </section>

      <section id="checkup" className="py-24 lg:py-32">
        <div className="container-tsm max-w-[1000px]">
          <FadeIn>
            <CheckupForm />
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-6 max-w-3xl text-xs italic leading-relaxed text-ink/50">
              The Business Document Checkup is a general business conversation. It is not legal advice, and it does
              not replace guidance from qualified legal, insurance, accounting, or regulatory professionals. Please
              don't include sensitive personal identifiers or protected health information in this form.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
